import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useTranslation } from '../../../shared/i18n';
import { familyCurrenciesApi } from '../api/familyCurrencies';
import { CurrencyTransferPanel } from './CurrencyTransferPanel';
import { WALLET_NOTIFICATION_COUNT_QUERY_KEY } from '../../unified-dashboard/hooks/useWalletNotificationCount';

const fetchFamilyCurrencies = async () => {
  const response = await familyCurrenciesApi.list();
  return response.data;
};

const CurrencyRow = ({ currency, onEdit, onDelete, onMint, onWallet, busy, t, walletOpen }) => (
  <div className="bg-light rounded-2 p-3 mb-3" data-testid={`currency-row-${currency.id}`}>
    <div className="d-flex flex-wrap align-items-center gap-2">
      <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
        <div
          className="d-inline-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
          data-testid={`currency-icon-${currency.id}`}
          data-fusion={currency.wallet_type === 'fusion' ? 'true' : 'false'}
          data-wallet-type={currency.wallet_type || 'positive'}
          style={{
            width: '40px',
            height: '40px',
            background: currency.wallet_type === 'fusion'
              ? 'linear-gradient(135deg, #fde68a 0%, #f59e0b 100%)'
              : currency.wallet_type === 'negative'
              ? 'linear-gradient(135deg, #fecaca 0%, #dc2626 100%)'
              : 'linear-gradient(135deg, #bbf7d0 0%, #16a34a 100%)'
          }}
        >
          <i
            className={`bi ${currency.wallet_type === 'fusion' ? 'bi-magic' : currency.wallet_type === 'negative' ? 'bi-x-lg' : 'bi-check-lg'} text-white`}
            style={{ fontSize: '22px' }}
          ></i>
        </div>
        <div className="flex-grow-1 min-w-0">
          <h5 className="fw-semibold mb-0 text-truncate">
            {currency.name}
          </h5>
          <small className="text-muted" data-testid={`balance-currency-${currency.id}`}>
            {t('family_currencies.balance', { balance: currency.balance ?? 0 })}
          </small>
          {currency.wallet_type === 'fusion' && (
            <div className="small text-muted mt-1" data-testid={`fusion-info-${currency.id}`}>
              {t('family_currencies.fusion.components_label')}:{' '}
              {(currency.fusion_components || []).map((c) => c.name).join(', ')}
            </div>
          )}
        </div>
      </div>
      <div
        className="d-flex flex-wrap align-items-center gap-1 ms-auto justify-content-end"
        style={{ maxWidth: '100%' }}
      >
        <button
          type="button"
          className="btn btn-sm btn-outline-primary flex-shrink-0"
          onClick={() => onWallet(currency)}
          disabled={busy}
          aria-label={walletOpen ? t('family_currencies.wallet.close') : t('family_currencies.wallet.open')}
          title={walletOpen ? t('family_currencies.wallet.close') : t('family_currencies.wallet.open')}
          data-testid={`wallet-currency-${currency.id}`}
        >
          <i className={`bi ${walletOpen ? 'bi-x-lg' : 'bi-arrow-left-right'}`}></i>
          <span className="d-none d-md-inline ms-1">
            {walletOpen ? t('family_currencies.wallet.close') : t('family_currencies.wallet.open')}
          </span>
        </button>
        <Link
          to={`/family-currencies/${currency.id}/transactions`}
          className="btn btn-sm btn-outline-secondary flex-shrink-0"
          aria-label={t('family_currencies.transactions.link')}
          title={t('family_currencies.transactions.link')}
          data-testid={`transactions-link-${currency.id}`}
        >
          <i className="bi bi-clock-history"></i>
          <span className="d-none d-md-inline ms-1">
            {t('family_currencies.transactions.link')}
          </span>
        </Link>
        {currency.wallet_type !== 'fusion' && (
          <button
            type="button"
            className="btn btn-sm btn-outline-success flex-shrink-0"
            onClick={() => onMint(currency)}
            disabled={busy}
            aria-label={t('family_currencies.mint')}
            title={t('family_currencies.mint')}
            data-testid={`mint-currency-${currency.id}`}
          >
            <i className="bi bi-hammer"></i>
            <span className="d-none d-md-inline ms-1">{t('family_currencies.mint')}</span>
          </button>
        )}
        <button
          type="button"
          className="btn btn-sm btn-link text-primary p-1 flex-shrink-0"
          onClick={() => onEdit(currency)}
          disabled={busy}
          aria-label={t('family_currencies.edit')}
          title={t('family_currencies.edit')}
          data-testid={`edit-currency-${currency.id}`}
        >
          <i className="bi bi-pencil"></i>
        </button>
        <button
          type="button"
          className="btn btn-sm btn-link text-danger p-1 flex-shrink-0"
          onClick={() => onDelete(currency)}
          disabled={busy}
          aria-label={t('family_currencies.remove')}
          title={t('family_currencies.remove')}
          data-testid={`delete-currency-${currency.id}`}
        >
          <i className="bi bi-trash"></i>
        </button>
      </div>
    </div>
  </div>
);

const MintBurnDialog = ({ currency, onMint, onBurn, onClose, busy, t }) => {
  const [tab, setTab] = useState('mint');
  const [amount, setAmount] = useState('');

  const isMint = tab === 'mint';
  const parsed = parseInt(amount, 10);
  const validAmount = Number.isFinite(parsed) && parsed > 0;
  const overBalance = !isMint && validAmount && parsed > (currency.balance ?? 0);
  const canSubmit = !busy && validAmount && !overBalance;

  const submit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (isMint) onMint(currency, parsed);
    else onBurn(currency, parsed);
  };

  const switchTab = (next) => {
    if (next === tab) return;
    setTab(next);
    setAmount('');
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      data-testid={`mint-burn-dialog-${currency.id}`}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <form onSubmit={submit}>
            <div className="modal-header">
              <h5 className="modal-title">
                {t('family_currencies.mint_burn_dialog_title', { name: currency.name })}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                disabled={busy}
                aria-label={t('family_currencies.mint_cancel')}
                data-testid={`mint-burn-dialog-close-${currency.id}`}
              ></button>
            </div>
            <div className="modal-body">
              <ul className="nav nav-tabs mb-3" role="tablist">
                <li className="nav-item" role="presentation">
                  <button
                    type="button"
                    className={`nav-link ${isMint ? 'active' : ''}`}
                    onClick={() => switchTab('mint')}
                    disabled={busy}
                    data-testid={`mint-tab-${currency.id}`}
                  >
                    {t('family_currencies.mint')}
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    type="button"
                    className={`nav-link ${!isMint ? 'active' : ''}`}
                    onClick={() => switchTab('burn')}
                    disabled={busy}
                    data-testid={`burn-tab-${currency.id}`}
                  >
                    {t('family_currencies.burn')}
                  </button>
                </li>
              </ul>

              <div className="mb-2 small text-muted">
                {t('family_currencies.balance', { balance: currency.balance ?? 0 })}
              </div>
              <label className="form-label fw-semibold" htmlFor={`mint-burn-amount-${currency.id}`}>
                {isMint
                  ? t('family_currencies.mint_amount_label')
                  : t('family_currencies.burn_amount_label')}
              </label>
              <input
                type="number"
                id={`mint-burn-amount-${currency.id}`}
                className="form-control"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="1"
                step="1"
                required
                autoFocus
                disabled={busy}
                data-testid={isMint
                  ? `mint-amount-input-${currency.id}`
                  : `burn-amount-input-${currency.id}`}
              />
              {overBalance && (
                <div className="text-danger small mt-2" data-testid={`burn-over-balance-${currency.id}`}>
                  {t('family_currencies.burn_insufficient_balance')}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onClose}
                disabled={busy}
              >
                {t('family_currencies.mint_cancel')}
              </button>
              <button
                type="submit"
                className={`btn ${isMint ? 'btn-success' : 'btn-danger'}`}
                disabled={!canSubmit}
                data-testid={isMint
                  ? `mint-submit-${currency.id}`
                  : `burn-submit-${currency.id}`}
              >
                {isMint
                  ? t('family_currencies.mint_submit')
                  : t('family_currencies.burn_submit')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const FUSION_DELAY_OPTIONS = [
  { value: 0, key: 'immediate' },
  { value: 1, key: 'one_minute' },
  { value: 5, key: 'five_minutes' },
  { value: 30, key: 'thirty_minutes' },
  { value: 60, key: 'one_hour' },
  { value: 120, key: 'two_hours' },
  { value: 240, key: 'four_hours' },
  { value: 360, key: 'six_hours' },
  { value: 480, key: 'eight_hours' },
  { value: 720, key: 'twelve_hours' }
];

const FusionDelaySelect = ({ id, value, onChange, disabled, t, testId }) => (
  <select
    id={id}
    className="form-select"
    value={value ?? 0}
    onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
    disabled={disabled}
    data-testid={testId}
  >
    {FUSION_DELAY_OPTIONS.map((opt) => (
      <option key={opt.value} value={opt.value}>
        {t(`family_currencies.fusion.reward_delay_options.${opt.key}`)}
      </option>
    ))}
  </select>
);

const EditCurrencyRow = ({ currency, componentOptions, onSave, onCancel, busy, t }) => {
  const [name, setName] = useState(currency.name);
  const [walletType, setWalletType] = useState(currency.wallet_type || 'positive');
  const [selectedComponentIds, setSelectedComponentIds] = useState(
    (currency.fusion_components || []).map((c) => c.id)
  );
  const [rewardDelayMinutes, setRewardDelayMinutes] = useState(
    currency.fusion_reward_delay_minutes ?? 0
  );

  const toggleComponent = (id) => {
    setSelectedComponentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const isFusion = walletType === 'fusion';
  const fusionUnavailable = componentOptions.length === 0;
  const canSubmit = name.trim() && (!isFusion || selectedComponentIds.length > 0);

  return (
    <form
      className="bg-light rounded-2 p-3 mb-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(currency, {
          name: name.trim(),
          wallet_type: walletType,
          component_currency_ids: isFusion ? selectedComponentIds : undefined,
          fusion_reward_delay_minutes: isFusion ? rewardDelayMinutes : undefined
        });
      }}
      data-testid={`edit-currency-form-${currency.id}`}
    >
      <div className="mb-2">
        <label className="form-label small fw-semibold" htmlFor={`edit-currency-name-${currency.id}`}>
          {t('family_currencies.edit_name_label')}
        </label>
        <input
          type="text"
          id={`edit-currency-name-${currency.id}`}
          className="form-control"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={50}
          required
          autoFocus
          disabled={busy}
        />
      </div>
      <div className="mb-3" data-testid={`edit-wallet-type-picker-${currency.id}`}>
        <div className="form-label small fw-semibold">
          {t('family_currencies.wallet_type.label')}
        </div>
        {['positive', 'negative', 'fusion'].map((type) => {
          const disabled = busy || (type === 'fusion' && fusionUnavailable && walletType !== 'fusion');
          return (
            <div className="form-check mb-2" key={type}>
              <input
                type="radio"
                className="form-check-input"
                id={`edit-wallet-type-${currency.id}-${type}`}
                name={`edit-wallet-type-${currency.id}`}
                value={type}
                checked={walletType === type}
                onChange={() => setWalletType(type)}
                disabled={disabled}
                data-testid={`edit-wallet-type-radio-${currency.id}-${type}`}
              />
              <label
                className="form-check-label"
                htmlFor={`edit-wallet-type-${currency.id}-${type}`}
              >
                {t(`family_currencies.wallet_type.${type}`)}
              </label>
              <div
                className="form-text mt-0"
                data-testid={`edit-wallet-type-description-${currency.id}-${type}`}
              >
                {t(`family_currencies.wallet_type.${type}_description`)}
              </div>
            </div>
          );
        })}
        {isFusion && fusionUnavailable && (
          <div
            className="form-text text-warning"
            data-testid={`edit-fusion-no-components-hint-${currency.id}`}
          >
            {t('family_currencies.fusion.no_components_hint')}
          </div>
        )}
      </div>
      {isFusion && (
        <div className="mb-3 p-3 border rounded-2" data-testid={`edit-fusion-components-${currency.id}`}>
          <div className="form-label small fw-semibold">
            {t('family_currencies.fusion.components_label')}
          </div>
          {componentOptions.map((c) => (
            <div className="form-check" key={c.id}>
              <input
                type="checkbox"
                className="form-check-input"
                id={`edit-fusion-component-${currency.id}-${c.id}`}
                checked={selectedComponentIds.includes(c.id)}
                onChange={() => toggleComponent(c.id)}
                disabled={busy}
                data-testid={`edit-fusion-component-checkbox-${currency.id}-${c.id}`}
              />
              <label
                className="form-check-label"
                htmlFor={`edit-fusion-component-${currency.id}-${c.id}`}
              >
                {c.name}
              </label>
            </div>
          ))}
          <div className="mt-3">
            <label
              className="form-label small fw-semibold"
              htmlFor={`edit-fusion-reward-delay-${currency.id}`}
            >
              {t('family_currencies.fusion.reward_delay_label')}
            </label>
            <FusionDelaySelect
              id={`edit-fusion-reward-delay-${currency.id}`}
              value={rewardDelayMinutes}
              onChange={setRewardDelayMinutes}
              disabled={busy}
              t={t}
              testId={`edit-fusion-reward-delay-${currency.id}`}
            />
            <div className="form-text">
              {t('family_currencies.fusion.reward_delay_help')}
            </div>
          </div>
        </div>
      )}
      <div className="d-flex gap-2">
        <button type="submit" className="btn btn-primary" disabled={busy || !canSubmit}>
          {t('common.save')}
        </button>
        <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
};

export const FamilyCurrenciesPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState('');
  // "positive" | "negative" | "fusion" — mirrors FamilyCurrency::WALLET_TYPES.
  const [walletType, setWalletType] = useState('positive');
  const isFusion = walletType === 'fusion';
  const [fusionRewardDelayMinutes, setFusionRewardDelayMinutes] = useState(0);
  const [fusionComponents, setFusionComponents] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [mintingId, setMintingId] = useState(null);
  const [walletOpenId, setWalletOpenId] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const { data, isLoading, error: fetchError, isFetched } = useQuery({
    queryKey: ['familyCurrencies'],
    queryFn: fetchFamilyCurrencies,
    refetchOnWindowFocus: true,
    refetchOnMount: true
  });

  // The list endpoint resets the wallet-notification counter server-side; tell
  // the count query to refetch once the listing has loaded so the badge clears
  // even if the broadcast was missed during the initial mount.
  useEffect(() => {
    if (isFetched) {
      queryClient.invalidateQueries({ queryKey: WALLET_NOTIFICATION_COUNT_QUERY_KEY });
    }
  }, [isFetched, queryClient]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['familyCurrencies'] });

  const handleApiError = (err) => {
    setError(err.response?.data?.error?.message || t('family_currencies.errors.generic'));
  };

  const resetAddForm = () => {
    setNewName('');
    setWalletType('positive');
    setFusionRewardDelayMinutes(0);
    setFusionComponents([]);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    resetAddForm();
    setError(null);
  };

  const createMutation = useMutation({
    mutationFn: (payload) => familyCurrenciesApi.create(payload),
    onSuccess: (response) => {
      resetAddForm();
      setShowAddModal(false);
      setError(null);
      setSuccessMessage(response.data?.message || null);
      invalidate();
    },
    onError: handleApiError
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, attrs }) => familyCurrenciesApi.update(id, attrs),
    onSuccess: (response) => {
      setEditingId(null);
      setError(null);
      setSuccessMessage(response.data?.message || null);
      invalidate();
    },
    onError: handleApiError
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => familyCurrenciesApi.remove(id),
    onSuccess: (response) => {
      setError(null);
      setSuccessMessage(response.data?.message || null);
      invalidate();
    },
    onError: handleApiError
  });

  const mintMutation = useMutation({
    mutationFn: ({ id, amount }) => familyCurrenciesApi.mint(id, amount),
    onSuccess: (response) => {
      setMintingId(null);
      setError(null);
      setSuccessMessage(response.data?.message || null);
      invalidate();
    },
    onError: handleApiError
  });

  const burnMutation = useMutation({
    mutationFn: ({ id, amount }) => familyCurrenciesApi.burn(id, amount),
    onSuccess: (response) => {
      setMintingId(null);
      setError(null);
      setSuccessMessage(response.data?.message || null);
      invalidate();
    },
    onError: handleApiError
  });

  const settingsMutation = useMutation({
    mutationFn: (settings) => familyCurrenciesApi.updateSettings(settings),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: handleApiError
  });

  const busy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending || mintMutation.isPending || burnMutation.isPending;

  const currencies = data?.family_currencies || [];
  const maxPerFamily = data?.max_per_family ?? 10;
  const atLimit = currencies.length >= maxPerFamily;
  const allowKidToKidTransfers = data?.settings?.allow_kid_to_kid_transfers ?? false;

  if (isLoading) {
    return (
      <div className="container py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger">
          {t('common.error')}: {fetchError.message}
        </div>
      </div>
    );
  }

  const handleCreate = (e) => {
    e.preventDefault();
    const trimmed = newName.trim();
    if (!trimmed) return;
    if (isFusion) {
      if (!fusionComponents.length) return;
      createMutation.mutate({
        name: trimmed,
        wallet_type: 'fusion',
        fusion_reward_delay_minutes: fusionRewardDelayMinutes,
        component_currency_ids: fusionComponents
      });
    } else {
      createMutation.mutate({ name: trimmed, wallet_type: walletType });
    }
  };

  const toggleFusionComponent = (id) => {
    setFusionComponents((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const nonFusionCurrencies = currencies.filter((c) => c.wallet_type !== 'fusion');

  const handleSaveEdit = (currency, {
    name,
    wallet_type,
    component_currency_ids,
    fusion_reward_delay_minutes
  }) => {
    if (!name) return;
    const currentWalletType = currency.wallet_type || 'positive';
    const nextWalletType = wallet_type || currentWalletType;
    const nextIsFusion = nextWalletType === 'fusion';
    const walletTypeChanged = nextWalletType !== currentWalletType;
    const currentComponentIds = (currency.fusion_components || [])
      .map((c) => c.id)
      .slice()
      .sort();
    const nextComponentIds = (component_currency_ids ?? []).slice().sort();
    const componentsChanged =
      nextIsFusion &&
      (nextComponentIds.length !== currentComponentIds.length ||
        nextComponentIds.some((id, i) => id !== currentComponentIds[i]));
    const currentDelay = currency.fusion_reward_delay_minutes ?? 0;
    const nextDelay = fusion_reward_delay_minutes ?? 0;
    const delayChanged = nextIsFusion && nextDelay !== currentDelay;
    if (
      name === currency.name &&
      !walletTypeChanged &&
      !componentsChanged &&
      !delayChanged
    ) {
      setEditingId(null);
      return;
    }
    const attrs = { name, wallet_type: nextWalletType };
    if (nextIsFusion) {
      attrs.component_currency_ids = component_currency_ids ?? [];
      attrs.fusion_reward_delay_minutes = nextDelay;
    }
    updateMutation.mutate({ id: currency.id, attrs });
  };

  const handleDelete = (currency) => {
    if (!window.confirm(t('family_currencies.confirm_remove', { name: currency.name }))) {
      return;
    }
    deleteMutation.mutate(currency.id);
  };

  const handleMint = (currency, amount) => {
    mintMutation.mutate({ id: currency.id, amount });
  };

  const handleBurn = (currency, amount) => {
    burnMutation.mutate({ id: currency.id, amount });
  };

  const mintingCurrency = mintingId
    ? currencies.find((c) => c.id === mintingId)
    : null;

  return (
    <div className="bg-light">
      <div className="container py-4" data-testid="family-currencies-page">
        <div className="mb-4">
          <h2 className="h1 fw-bold mb-2">{t('family_currencies.page.title')}</h2>
          <p className="text-muted">
            {t('family_currencies.page.description', { max: maxPerFamily })}
          </p>
        </div>

        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            {successMessage}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccessMessage(null)}
              aria-label="Close"
            ></button>
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert" data-testid="family-currencies-error">
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label="Close"
            ></button>
          </div>
        )}

        <div className="row">
          <div className="col-lg-8">
            <div className="card border rounded-3 mb-4" data-testid="family-currency-settings-card">
              <div className="card-body p-4">
                <div className="d-flex align-items-start gap-3 mb-3">
                  <div className="flex-grow-1">
                    <div className="fw-semibold">
                      {t('auto_reward_rules.entry.title')}
                    </div>
                    <p className="text-muted small mb-0">
                      {t('auto_reward_rules.entry.description')}
                    </p>
                  </div>
                  <div className="flex-shrink-0" style={{ minWidth: '3em', textAlign: 'right' }}>
                    <Link
                      to="/family-currencies/auto-reward-rules"
                      className="text-primary text-decoration-none"
                      aria-label={t('auto_reward_rules.entry.open')}
                      title={t('auto_reward_rules.entry.open')}
                      data-testid="open-auto-reward-rules"
                    >
                      <i className="bi bi-gear-fill"></i>
                    </Link>
                  </div>
                </div>
                <hr className="my-3" />
                <div className="d-flex align-items-start gap-3">
                  <div className="flex-grow-1">
                    <label className="fw-semibold mb-0" htmlFor="allow-kid-to-kid-transfers">
                      {t('family_currencies.settings.allow_kid_to_kid_transfers')}
                    </label>
                    <p className="text-muted small mb-0">
                      {t('family_currencies.settings.allow_kid_to_kid_transfers_help')}
                    </p>
                  </div>
                  <div className="form-check form-switch flex-shrink-0 m-0 ps-0" style={{ minWidth: '3em', textAlign: 'right' }}>
                    <input
                      id="allow-kid-to-kid-transfers"
                      className="form-check-input float-none m-0"
                      type="checkbox"
                      role="switch"
                      checked={allowKidToKidTransfers}
                      disabled={busy || settingsMutation.isPending}
                      onChange={(e) =>
                        settingsMutation.mutate({ allow_kid_to_kid_transfers: e.target.checked })
                      }
                      data-testid="allow-kid-to-kid-transfers-toggle"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="card border rounded-3 mb-4" data-testid="family-currency-list-card">
              <div className="card-body p-4">
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <div>
                    <h3 className="h5 fw-bold mb-1">{t('family_currencies.list_title')}</h3>
                    <p className="text-muted small mb-0">
                      {t('family_currencies.list_count', { count: currencies.length, max: maxPerFamily })}
                    </p>
                  </div>
                </div>

                {currencies.length === 0 && (
                  <p className="text-muted">{t('family_currencies.empty')}</p>
                )}

                {currencies.map((currency) => {
                  if (editingId === currency.id) {
                    return (
                      <EditCurrencyRow
                        key={currency.id}
                        currency={currency}
                        componentOptions={nonFusionCurrencies.filter((c) => c.id !== currency.id)}
                        onSave={handleSaveEdit}
                        onCancel={() => setEditingId(null)}
                        busy={busy}
                        t={t}
                      />
                    );
                  }
                  return (
                    <div key={currency.id}>
                      <CurrencyRow
                        currency={currency}
                        onEdit={(c) => setEditingId(c.id)}
                        onDelete={handleDelete}
                        onMint={(c) => setMintingId(c.id)}
                        onWallet={(c) =>
                          setWalletOpenId((prev) => (prev === c.id ? null : c.id))
                        }
                        walletOpen={walletOpenId === currency.id}
                        busy={busy}
                        t={t}
                      />
                      {walletOpenId === currency.id && (
                        <div className="bg-white rounded-2 p-3 mb-3 border">
                          <CurrencyTransferPanel
                            currency={currency}
                            api={familyCurrenciesApi}
                            busy={busy}
                            onTransferred={invalidate}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className="mt-3">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setShowAddModal(true)}
                    disabled={busy || atLimit}
                    data-testid="add-currency-btn"
                  >
                    {t('family_currencies.add_button')}
                  </button>
                  {atLimit && (
                    <div className="form-text text-warning">
                      {t('family_currencies.limit_reached', { max: maxPerFamily })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {mintingCurrency && (
        <MintBurnDialog
          currency={mintingCurrency}
          onMint={handleMint}
          onBurn={handleBurn}
          onClose={() => setMintingId(null)}
          busy={busy}
          t={t}
        />
      )}

      {showAddModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          data-testid="add-currency-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget && !createMutation.isPending) closeAddModal();
          }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form onSubmit={handleCreate} data-testid="add-currency-form">
                <div className="modal-header">
                  <h5 className="modal-title">{t('family_currencies.add_label')}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeAddModal}
                    disabled={createMutation.isPending}
                    aria-label={t('common.cancel')}
                    data-testid="close-add-currency-modal"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="new_currency_name" className="form-label fw-semibold">
                      {t('family_currencies.add_label')}
                    </label>
                    <input
                      type="text"
                      id="new_currency_name"
                      className="form-control"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder={t('family_currencies.add_placeholder')}
                      maxLength={50}
                      disabled={createMutation.isPending}
                      autoFocus
                      data-testid="new-currency-input"
                    />
                  </div>
                  <div className="mb-3" data-testid="wallet-type-picker">
                    <div className="form-label fw-semibold">
                      {t('family_currencies.wallet_type.label')}
                    </div>
                    {['positive', 'negative', 'fusion'].map((type) => {
                      const disabled =
                        createMutation.isPending ||
                        (type === 'fusion' && nonFusionCurrencies.length === 0);
                      return (
                        <div className="form-check mb-2" key={type}>
                          <input
                            type="radio"
                            className="form-check-input"
                            id={`wallet_type_${type}`}
                            name="wallet_type"
                            value={type}
                            checked={walletType === type}
                            onChange={() => setWalletType(type)}
                            disabled={disabled}
                            data-testid={`wallet-type-radio-${type}`}
                          />
                          <label className="form-check-label" htmlFor={`wallet_type_${type}`}>
                            {t(`family_currencies.wallet_type.${type}`)}
                          </label>
                          <div
                            className="form-text mt-0"
                            data-testid={`wallet-type-description-${type}`}
                          >
                            {t(`family_currencies.wallet_type.${type}_description`)}
                          </div>
                        </div>
                      );
                    })}
                    {nonFusionCurrencies.length === 0 && (
                      <div className="form-text text-warning" data-testid="fusion-no-components-hint">
                        {t('family_currencies.fusion.no_components_hint')}
                      </div>
                    )}
                  </div>
                  {isFusion && (
                    <div className="mt-3 p-3 border rounded-2 bg-light" data-testid="fusion-config">
                      <div>
                        <div className="form-label fw-semibold">
                          {t('family_currencies.fusion.components_label')}
                        </div>
                        {nonFusionCurrencies.map((c) => (
                          <div className="form-check" key={c.id}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`fusion_component_${c.id}`}
                              checked={fusionComponents.includes(c.id)}
                              onChange={() => toggleFusionComponent(c.id)}
                              disabled={createMutation.isPending}
                              data-testid={`fusion-component-checkbox-${c.id}`}
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`fusion_component_${c.id}`}
                            >
                              {c.name}
                            </label>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3">
                        <label
                          className="form-label fw-semibold"
                          htmlFor="fusion_reward_delay_minutes"
                        >
                          {t('family_currencies.fusion.reward_delay_label')}
                        </label>
                        <FusionDelaySelect
                          id="fusion_reward_delay_minutes"
                          value={fusionRewardDelayMinutes}
                          onChange={setFusionRewardDelayMinutes}
                          disabled={createMutation.isPending}
                          t={t}
                          testId="fusion-reward-delay-input"
                        />
                        <div className="form-text">
                          {t('family_currencies.fusion.reward_delay_help')}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={closeAddModal}
                    disabled={createMutation.isPending}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={
                      createMutation.isPending ||
                      !newName.trim() ||
                      (isFusion && !fusionComponents.length)
                    }
                    data-testid="submit-add-currency-btn"
                  >
                    {t('family_currencies.submit_button')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyCurrenciesPage;
